import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import io from 'socket.io-client';
import Icon from './Icon';
import { useNow, elapsedSince, fmtTime } from './ui';
import './Monitor.css';
import 'leaflet/dist/leaflet.css';

// Leaflet Icon Fix
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const ROTATION_INTERVAL_MS = 12000;
const ROTATION_PAUSE_AFTER_CLICK_MS = 30000;

// Stilles 1-Frame-MP3 als Data-URL, dient nur zum Audio-Entsperren des Browsers
const SILENT_MP3 = 'data:audio/mp3;base64,/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

// ─── Map updater ─────────────────────────────────────────────────────────────
const MapUpdater = ({ einsatzKoordinaten, einsatzZoomLevel, kartenLocation }) => {
  const map = useMap();
  useEffect(() => {
    if (einsatzKoordinaten) {
      const zoomLevel = einsatzZoomLevel || 16;
      map.setView([einsatzKoordinaten.lat, einsatzKoordinaten.lng], zoomLevel);
    } else {
      map.setView([kartenLocation.lat, kartenLocation.lng], kartenLocation.zoom);
    }
  }, [einsatzKoordinaten, einsatzZoomLevel, kartenLocation, map]);
  return null;
};

const einsatzMarkerIcon = (highlighted) =>
  L.divIcon({
    className: 'custom-div-icon',
    html: `<div class="einsatz-marker${highlighted ? ' highlighted' : ' dimmed'}">🚨</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });

// ─── Einteilung View ─────────────────────────────────────────────────────────
const EinteilungFahrzeugCard = ({ fahrzeug, personen }) => {
  const visiblePersonen = personen.filter(p => p.name);
  return (
    <div className="einteilung-fz-card">
      <div className="einteilung-fz-header">
        <div className="einteilung-fz-funkruf">{fahrzeug.funkruf}</div>
        <div className="einteilung-fz-name">{fahrzeug.name}</div>
        <div className="einteilung-fz-count">{visiblePersonen.length} Pers.</div>
      </div>
      <div className="einteilung-personen-list">
        {visiblePersonen.length === 0 ? (
          <div className="einteilung-person-empty">—</div>
        ) : visiblePersonen.map((p, i) => (
          <div key={i} className="einteilung-person-row">
            <span className="einteilung-person-name">{p.name}</span>
            {p.funktion && <span className="einteilung-person-funktion">{p.funktion}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const EinteilungView = ({ einteilung, fahrzeuge }) => (
  <div className="einteilung-view">
    <div className="einteilung-hero">
      <div className="einteilung-label">Fahrzeugeinteilung</div>
      <div className="einteilung-name">{einteilung.name}</div>
      {einteilung.beschreibung && <div className="einteilung-sub">{einteilung.beschreibung}</div>}
    </div>
    <div className="einteilung-grid">
      {einteilung.fahrzeugZuweisungen.map(z => {
        const fz = fahrzeuge.find(f => f.id === z.fahrzeugId);
        if (!fz) return null;
        return <EinteilungFahrzeugCard key={z.fahrzeugId} fahrzeug={fz} personen={z.personen} />;
      })}
    </div>
  </div>
);

// ─── Monitor Header ──────────────────────────────────────────────────────────
const MonitorHeader = ({ now, alarmActive, einteilungActive, count }) => (
  <div className="mon-header">
    <div className="mon-brand">
      <div className="mon-logo">
        <Icon name="local_fire_department" size={28} color="#fff" />
      </div>
      <div>
        <div className="mon-brand-title">Einsatzmonitor</div>
        <div className="mon-brand-sub">Jugendfeuerwehr · Einheit Ka/Kö</div>
      </div>
    </div>
    <div className="mon-header-right">
      <div className={`mon-status-pill ${alarmActive ? 'alarm' : einteilungActive ? 'einteilung' : 'ready'}`}>
        <span className="mon-status-dot" />
        <span>
          {alarmActive
            ? count > 1 ? `${count} Einsätze aktiv` : 'Einsatz aktiv'
            : einteilungActive
            ? 'Einteilung aktiv'
            : 'Bereitschaft'}
        </span>
      </div>
      <div className="mon-clock">
        <div className="mon-clock-time">
          {now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
        <div className="mon-clock-date">
          {now.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      </div>
    </div>
  </div>
);

// ─── Tabs for multiple active alarms ─────────────────────────────────────────
const EinsatzTabs = ({ einsaetze, activeIndex, onSelect }) => (
  <div className="mon-tabs">
    {einsaetze.map((e, idx) => (
      <button
        key={e.id}
        type="button"
        className={`mon-tab${idx === activeIndex ? ' active' : ''}`}
        onClick={() => onSelect(idx)}
        title={e.stichwort}
      >
        <span className="mon-tab-index">{String(idx + 1).padStart(2, '0')}</span>
        <span className="mon-tab-label">{e.stichwort}</span>
      </button>
    ))}
  </div>
);

// ─── Fahrzeug Kachel ─────────────────────────────────────────────────────────
const FahrzeugKachel = ({ fahrzeug, einsatz }) => {
  const alarmiert = fahrzeug.status === 'alarmiert';
  return (
    <div className={`fz-kachel${alarmiert ? ' alarmiert' : ''}`}>
      {alarmiert && <div className="fz-kachel-pattern" />}
      <div className="fz-kachel-top">
        <div>
          <div className="fz-funkruf">{fahrzeug.funkruf || 'Funkruf —'}</div>
          <div className="fz-name">{fahrzeug.name}</div>
        </div>
        <div className="fz-icon-box">
          <Icon
            name={alarmiert ? 'warning' : 'check_circle'}
            size={20}
            color={alarmiert ? '#fff' : 'var(--green)'}
          />
        </div>
      </div>
      <div>
        <div className="fz-status-line">
          <span className={`fz-status-dot${alarmiert ? ' pulse' : ''}`} />
          {alarmiert ? 'Alarmiert' : 'Bereit'}
        </div>
        {einsatz && <div className="fz-einsatz-info">{einsatz.stichwort}</div>}
      </div>
    </div>
  );
};

// ─── Alarm View ──────────────────────────────────────────────────────────────
const AlarmView = ({ einsatz, fahrzeuge, einsaetze, kartenLocation, activeIndex, onMarkerClick, now }) => {
  const elapsed = elapsedSince(einsatz.ausloeseZeit || einsatz.timestamp, now);
  const alarmierteFz = einsatz.alarmierteFahrzeuge
    .map((fid) => fahrzeuge.find((f) => f.id === fid))
    .filter(Boolean);

  return (
    <div className="alarm-view">
      <div className="alarm-hero">
        <div className="alarm-ribbon" />
        <div className="alarm-tag-row">
          <div className="alarm-tag">Alarm</div>
          <div className="alarm-time">Einsatz · {fmtTime(einsatz.timestamp)} Uhr</div>
        </div>
        <div className="alarm-stichwort">{einsatz.stichwort}</div>
        <div className="alarm-address-row">
          <div className="alarm-icon-box">
            <Icon name="location_on" size={28} color="var(--amber)" />
          </div>
          <div>
            <div className="alarm-section-label">Einsatzort</div>
            <div className="alarm-address">{einsatz.adresse}</div>
          </div>
        </div>
        {einsatz.beschreibung && <div className="alarm-beschreibung">{einsatz.beschreibung}</div>}
        <div className="alarm-footer">
          <div>
            <div className="alarm-section-label">Seit Alarmierung</div>
            <div className="alarm-stoppuhr">{elapsed}</div>
          </div>
          {einsatz.mitteiler && (
            <div>
              <div className="alarm-section-label">Mitteiler</div>
              <div className="alarm-mitteiler">
                <Icon name="call" size={18} color="var(--text-mute)" />
                {einsatz.mitteiler}
                {einsatz.telefon && <span className="alarm-telefon"> · {einsatz.telefon}</span>}
              </div>
            </div>
          )}
          <div>
            <div className="alarm-section-label">Alarmiert · {alarmierteFz.length}</div>
            <div className="alarm-fz-chips">
              {alarmierteFz.map((f) => (
                <div key={f.id} className="alarm-fz-chip">
                  {f.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="alarm-map-box">
        <MapContainer
          center={[einsatz.koordinaten.lat, einsatz.koordinaten.lng]}
          zoom={einsatz.zoomLevel || 16}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          <MapUpdater
            einsatzKoordinaten={einsatz.koordinaten}
            einsatzZoomLevel={einsatz.zoomLevel}
            kartenLocation={kartenLocation}
          />
          {einsaetze.map((e, idx) =>
            e.koordinaten ? (
              <Marker
                key={e.id}
                position={[e.koordinaten.lat, e.koordinaten.lng]}
                icon={einsatzMarkerIcon(idx === activeIndex)}
                eventHandlers={{ click: () => onMarkerClick(idx) }}
              />
            ) : null
          )}
        </MapContainer>
        <div className="alarm-map-overlay">
          <span className="alarm-map-overlay-left">
            <Icon name="location_on" size={16} color="var(--amber)" />
            {einsatz.adresse}
          </span>
          <span className="alarm-map-overlay-right">
            {einsatz.koordinaten.lat.toFixed(4)}, {einsatz.koordinaten.lng.toFixed(4)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Bereitschafts View ──────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent }) => (
  <div className="stat-card">
    <div className="stat-card-label">{label}</div>
    <div className="stat-card-value" style={{ color: accent }}>
      {value}
    </div>
    <div className="stat-card-sub">{sub}</div>
  </div>
);

const isTerminAnstehend = (uhrzeit, now) => {
  if (!uhrzeit) return false;
  const [h, m] = uhrzeit.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return false;
  const total = h * 60 + m;
  const nowTotal = now.getHours() * 60 + now.getMinutes();
  return total >= nowTotal;
};

const TerminItem = ({ termin }) => (
  <div className="ready-termin">
    <div className="ready-termin-time">{termin.uhrzeit}</div>
    <div className="ready-termin-body">
      <div className="ready-termin-titel">{termin.titel}</div>
      {termin.beschreibung && <div className="ready-termin-desc">{termin.beschreibung}</div>}
    </div>
  </div>
);

const BereitschaftView = ({ fahrzeuge, einsaetze, kartenLocation, termine, now }) => {
  const anstehend = termine
    .filter((t) => isTerminAnstehend(t.uhrzeit, now))
    .sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''));

  return (
    <div className="ready-view">
      <div className="ready-hero">
        <div className="ready-label">Status</div>
        <div className="ready-title">Bereit­schaft</div>
        <div className="ready-sub">
          Keine aktiven Einsätze. Alle Fahrzeuge stehen einsatzbereit zur Verfügung.
        </div>
        <div className="ready-stats">
          <StatCard
            label="Fahrzeuge bereit"
            value={fahrzeuge.filter((f) => f.status === 'bereit').length}
            sub={`von ${fahrzeuge.length}`}
            accent="var(--green)"
          />
          <StatCard
            label="Beendete heute"
            value={einsaetze.filter((e) => e.status === 'beendet').length}
            sub="erledigt"
            accent="var(--text-dim)"
          />
        </div>
      </div>
      <div className="ready-right">
        {anstehend.length > 0 && (
          <div className="ready-termine">
            <div className="ready-termine-head">
              <Icon name="event" size={18} color="var(--amber)" />
              <span>Anstehende Termine</span>
              <span className="ready-termine-count">{anstehend.length}</span>
            </div>
            <div className="ready-termine-list">
              {anstehend.slice(0, 5).map((t) => (
                <TerminItem key={t.id} termin={t} />
              ))}
              {anstehend.length > 5 && (
                <div className="ready-termine-more">
                  + {anstehend.length - 5} weitere
                </div>
              )}
            </div>
          </div>
        )}
        <div className="ready-map-box">
          <MapContainer
            center={[kartenLocation.lat, kartenLocation.lng]}
            zoom={kartenLocation.zoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            <MapUpdater kartenLocation={kartenLocation} />
          </MapContainer>
          <div className="alarm-map-overlay">
            <span className="alarm-map-overlay-left">
              <Icon name="map" size={16} color="var(--green)" />
              Übersicht
            </span>
            <span className="alarm-map-overlay-right">
              {fahrzeuge.length} {fahrzeuge.length === 1 ? 'Fahrzeug' : 'Fahrzeuge'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Monitor (Top-level) ─────────────────────────────────────────────────────
const Monitor = () => {
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [einsaetzeAll, setEinsaetzeAll] = useState([]);
  const [aktiveEinsaetze, setAktiveEinsaetze] = useState([]);
  const [aktiverIndex, setAktiverIndex] = useState(0);
  const [termine, setTermine] = useState([]);
  const [einteilungen, setEinteilungen] = useState([]);
  const [activeEinteilungId, setActiveEinteilungId] = useState(null);
  const [kartenLocation, setKartenLocation] = useState({ lat: 50.9967, lng: 6.5711, zoom: 13 });

  const alarmAudioRef = useRef(null);
  const terminAudioRef = useRef(null);
  const gongSettingsRef = useRef({ alarmVolume: 0.8, alarmLoop: false, terminVolume: 0.8 });
  const audioUnlockedRef = useRef(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const pendingPlayRef = useRef(null); // {which:'alarm'|'termin'}
  const rotationPausedUntilRef = useRef(0);
  const now = useNow(1000);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const newSocket = io();

    fetchFahrzeuge();
    fetchEinsaetze();
    fetchTermine();
    fetchKartenLocation();
    fetchGongSettings();
    fetchEinteilungen();

    // Versuche stilles Auto-Unlock — klappt nur, wenn der Browser bereits eine Geste erkannt hat
    tryUnlockAudio();

    newSocket.on('fahrzeugeUpdated', (u) => setFahrzeuge(u));

    newSocket.on('einsatzErstellt', (e) => setEinsaetzeAll((prev) => (prev.some(x => x.id === e.id) ? prev : [...prev, e])));

    newSocket.on('einsatzAusgeloest', (e) => {
      setEinsaetzeAll((prev) => prev.map((x) => (x.id === e.id ? e : x)));
      setAktiveEinsaetze((prev) => {
        const idx = prev.findIndex((x) => x.id === e.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = e;
          setAktiverIndex(idx);
          return next;
        }
        const next = [...prev, e];
        setAktiverIndex(next.length - 1);
        return next;
      });
    });

    newSocket.on('einsatzBeendet', (e) => {
      setEinsaetzeAll((prev) => prev.map((x) => (x.id === e.id ? e : x)));
      setAktiveEinsaetze((prev) => prev.filter((x) => x.id !== e.id));
    });

    newSocket.on('einsatzGeloescht', (id) => {
      setEinsaetzeAll((prev) => prev.filter((x) => x.id !== id));
      setAktiveEinsaetze((prev) => prev.filter((x) => x.id !== id));
    });

    newSocket.on('einsatzAktualisiert', (e) => {
      setEinsaetzeAll((prev) => prev.map((x) => (x.id === e.id ? e : x)));
      setAktiveEinsaetze((prev) => {
        const idx = prev.findIndex((x) => x.id === e.id);
        if (idx < 0) return prev;
        if (e.status !== 'aktiv') return prev.filter((x) => x.id !== e.id);
        const next = [...prev];
        next[idx] = e;
        return next;
      });
    });

    newSocket.on('termineUpdated', (t) => setTermine(t));
    newSocket.on('kartenLocationUpdated', (loc) => setKartenLocation(loc));
    newSocket.on('einteilungenUpdated', ({ einteilungen: e, activeEinteilungId: aid }) => {
      setEinteilungen(e);
      setActiveEinteilungId(aid);
    });
    newSocket.on('einteilungAktiviert', ({ einteilung, activeEinteilungId: aid }) => {
      setEinteilungen(prev => prev.map(x => x.id === einteilung.id ? einteilung : { ...x, status: 'entwurf' }));
      setActiveEinteilungId(aid);
    });
    newSocket.on('einteilungDeaktiviert', () => {
      setEinteilungen(prev => prev.map(x => ({ ...x, status: 'entwurf' })));
      setActiveEinteilungId(null);
    });
    newSocket.on('gongSettingsUpdated', (s) => { gongSettingsRef.current = { ...gongSettingsRef.current, ...s }; });
    newSocket.on('playGongSound', () => playAlarmGong());
    newSocket.on('playTerminGong', () => playTerminGong());
    newSocket.on('stopGongSound', () => stopAllGongs());

    const interval = setInterval(() => {
      fetchFahrzeuge();
      fetchEinsaetze();
      fetchEinteilungen();
    }, 10000);

    return () => {
      newSocket.close();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (aktiveEinsaetze.length <= 1) return undefined;
    const interval = setInterval(() => {
      if (Date.now() < rotationPausedUntilRef.current) return;
      setAktiverIndex((prev) => (prev + 1) % aktiveEinsaetze.length);
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [aktiveEinsaetze.length]);

  useEffect(() => {
    if (aktiverIndex >= aktiveEinsaetze.length && aktiveEinsaetze.length > 0) {
      setAktiverIndex(aktiveEinsaetze.length - 1);
    } else if (aktiveEinsaetze.length === 0 && aktiverIndex !== 0) {
      setAktiverIndex(0);
    }
  }, [aktiveEinsaetze.length, aktiverIndex]);

  const fetchFahrzeuge = async () => {
    try {
      const r = await axios.get('/api/fahrzeuge');
      setFahrzeuge(r.data);
    } catch (e) {
      console.error('Fehler beim Laden der Fahrzeuge:', e);
    }
  };

  const fetchTermine = async () => {
    try {
      const r = await axios.get('/api/termine');
      setTermine(r.data || []);
    } catch (e) {
      console.error('Fehler beim Laden der Termine:', e);
    }
  };

  const fetchKartenLocation = async () => {
    try {
      const r = await axios.get('/api/karten-location');
      setKartenLocation(r.data);
    } catch (e) {
      console.error('Fehler beim Laden der Karten-Location:', e);
    }
  };

  const fetchEinteilungen = async () => {
    try {
      const r = await axios.get('/api/einteilungen');
      setEinteilungen(r.data.einteilungen || []);
      setActiveEinteilungId(r.data.activeEinteilungId);
    } catch (e) {
      console.error('Fehler beim Laden der Einteilungen:', e);
    }
  };

  const fetchGongSettings = async () => {
    try {
      const r = await axios.get('/api/gong-settings');
      gongSettingsRef.current = { ...gongSettingsRef.current, ...r.data };
    } catch (e) {
      console.error('Fehler beim Laden der Gong-Einstellungen:', e);
    }
  };

  const fetchEinsaetze = async () => {
    try {
      const r = await axios.get('/api/einsaetze');
      const all = r.data || [];
      setEinsaetzeAll(all);
      const aktive = all.filter((e) => e.status === 'aktiv');
      setAktiveEinsaetze((prev) => {
        const existingIds = prev.map((e) => e.id);
        const sorted = [];
        existingIds.forEach((id) => {
          const found = aktive.find((e) => e.id === id);
          if (found) sorted.push(found);
        });
        aktive.forEach((e) => {
          if (!existingIds.includes(e.id)) sorted.push(e);
        });
        return sorted;
      });
    } catch (e) {
      console.error('Fehler beim Laden der Einsätze:', e);
    }
  };

  // Audio-Entsperrung via Nutzer-Geste
  const tryUnlockAudio = async () => {
    try {
      // AudioContext.resume() ist die zuverlässigste Methode – braucht keine gültige Audiodatei
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        await ctx.resume();
        ctx.close();
      } else {
        // Fallback für sehr alte Browser
        const a = new Audio(SILENT_MP3);
        a.volume = 0;
        await a.play();
        a.pause();
      }
      audioUnlockedRef.current = true;
      setAudioUnlocked(true);
      // Falls ein Gong-Trigger wartete, jetzt nachholen
      if (pendingPlayRef.current === 'alarm') playAlarmGong();
      else if (pendingPlayRef.current === 'termin') playTerminGong();
      pendingPlayRef.current = null;
      return true;
    } catch (e) {
      console.warn('Audio-Unlock fehlgeschlagen:', e);
      // War kein Autoplay-Block, sondern ein anderer Fehler → Geste war trotzdem gültig
      if (e.name !== 'NotAllowedError') {
        audioUnlockedRef.current = true;
        setAudioUnlocked(true);
        if (pendingPlayRef.current === 'alarm') playAlarmGong();
        else if (pendingPlayRef.current === 'termin') playTerminGong();
        pendingPlayRef.current = null;
        return true;
      }
      return false;
    }
  };

  const stopAudioInstance = (ref) => {
    if (!ref.current) return;
    try {
      ref.current.pause();
      ref.current.currentTime = 0;
      ref.current.loop = false;
    } catch (e) { /* ignore */ }
    ref.current = null;
  };

  const playAlarmGong = () => {
    stopAudioInstance(alarmAudioRef);
    const { alarmVolume, alarmLoop } = gongSettingsRef.current;
    const audio = new Audio(`/api/gong-audio?t=${Date.now()}`);
    audio.volume = Math.max(0, Math.min(1, alarmVolume ?? 0.8));
    audio.loop = !!alarmLoop;
    audio.addEventListener('ended', () => {
      if (alarmAudioRef.current === audio) alarmAudioRef.current = null;
    });
    alarmAudioRef.current = audio;
    audio.play().catch((err) => {
      console.error('Alarm-Gong play() blockiert:', err);
      if (err && err.name === 'NotAllowedError') {
        audioUnlockedRef.current = false;
        setAudioUnlocked(false);
        pendingPlayRef.current = 'alarm';
      }
    });
  };

  const playTerminGong = () => {
    stopAudioInstance(terminAudioRef);
    const { terminVolume } = gongSettingsRef.current;
    const audio = new Audio(`/api/termin-gong-audio?t=${Date.now()}`);
    audio.volume = Math.max(0, Math.min(1, terminVolume ?? 0.8));
    audio.loop = false;
    audio.addEventListener('ended', () => {
      if (terminAudioRef.current === audio) terminAudioRef.current = null;
    });
    terminAudioRef.current = audio;
    audio.play().catch((err) => {
      console.error('Termin-Gong play() blockiert:', err);
      if (err && err.name === 'NotAllowedError') {
        audioUnlockedRef.current = false;
        setAudioUnlocked(false);
        pendingPlayRef.current = 'termin';
      }
    });
  };

  const stopAllGongs = () => {
    stopAudioInstance(alarmAudioRef);
    stopAudioInstance(terminAudioRef);
    pendingPlayRef.current = null;
  };

  const handleTabClick = (idx) => {
    setAktiverIndex(idx);
    rotationPausedUntilRef.current = Date.now() + ROTATION_PAUSE_AFTER_CLICK_MS;
  };

  const alarmActive = aktiveEinsaetze.length > 0;
  const aktiverEinsatz = aktiveEinsaetze[aktiverIndex];

  return (
    <div className="monitor-root">
      {!audioUnlocked && (
        <div
          className="audio-unlock-overlay"
          onClick={tryUnlockAudio}
          role="button"
          tabIndex={0}
        >
          <div className="audio-unlock-box">
            <Icon name="volume_up" size={56} color="var(--amber)" />
            <h2>Audio aktivieren</h2>
            <p>
              Der Browser blockiert Audio-Wiedergabe, bis er einmal eine Interaktion sieht.
              Bitte einmal klicken, damit Alarm- und Termin-Gong abgespielt werden können.
            </p>
            <div className="audio-unlock-btn">
              <Icon name="touch_app" size={20} /> Klicken zum Aktivieren
            </div>
          </div>
        </div>
      )}

      {(() => {
        const aktiveEinteilung = activeEinteilungId
          ? einteilungen.find(e => e.id === activeEinteilungId) || null
          : null;
        return (
          <>
            <MonitorHeader
              now={now}
              alarmActive={alarmActive}
              einteilungActive={!!aktiveEinteilung}
              count={aktiveEinsaetze.length}
            />

            {aktiveEinsaetze.length > 1 && (
              <EinsatzTabs einsaetze={aktiveEinsaetze} activeIndex={aktiverIndex} onSelect={handleTabClick} />
            )}

            {alarmActive && aktiverEinsatz ? (
              <AlarmView
                einsatz={aktiverEinsatz}
                fahrzeuge={fahrzeuge}
                einsaetze={aktiveEinsaetze}
                kartenLocation={kartenLocation}
                activeIndex={aktiverIndex}
                onMarkerClick={handleTabClick}
                now={now}
              />
            ) : aktiveEinteilung ? (
              <EinteilungView einteilung={aktiveEinteilung} fahrzeuge={fahrzeuge} />
            ) : (
              <BereitschaftView
                fahrzeuge={fahrzeuge}
                einsaetze={einsaetzeAll}
                kartenLocation={kartenLocation}
                termine={termine}
                now={now}
              />
            )}
          </>
        );
      })()}

      <div className="fz-bar">
        {fahrzeuge.map((f) => {
          const einsatz = aktiveEinsaetze.find((e) => e.alarmierteFahrzeuge.includes(f.id));
          return <FahrzeugKachel key={f.id} fahrzeug={f} einsatz={einsatz} />;
        })}
        {fahrzeuge.length === 0 && <div className="fz-empty">Keine Fahrzeuge registriert</div>}
      </div>
    </div>
  );
};

export default Monitor;
